/** @format */

import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import InputAdornment from '@material-ui/core/InputAdornment';
import Icon from '@material-ui/core/Icon';

import GridContainer from '../../components/Grid/GridContainer.jsx';
import GridItem from '../../components/Grid/GridItem.jsx';
import CustomInputPassword from '../../components/CustomInput/CustomInputPassword';
import Button from '../../components/CustomButtons/ButtonExt';

import I18n from '../../components/Translate/I18n';

import { authActions } from '../../modules/auth/actions';

import loginPageStyle from '../../assets/jss/material-dashboard-pro-react/views/loginPageStyle';
import { AuthReducersType } from '../../modules/auth/reducer';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import i18n from 'i18next';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { Auth } from '../../modules/auth/Auth';
import { Timer } from '../../core/timers/Timer';
import IconButton from '@material-ui/core/IconButton';
import { Close } from 'mdi-material-ui';
import {ReactComponent as LockIcon} from '../../assets/themes/OPL/img/icons/Lock.svg';

const useStyle = makeStyles(loginPageStyle);

declare global {
    interface Window {
        temporaryPasswordCustomSnackBarFix_DisableDefaultSnackbar: boolean;
    }
}

const PasswordChangeModal = (): React.ReactElement => {
    const classes = useStyle();
    const dispatch = useDispatch();
    const [currentPassword, setCurrentPassword] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [repeatNewPassword, setRepeatNewPassword] = useState<string>('');
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { passwordModalOpen, disableModalClose, modalModifiedSubTitle } = useSelector(
        (state: { auth: AuthReducersType }) => state.auth,
        shallowEqual
    );

    const timer: Timer = useMemo(() => {
        return new Timer({
            identifier: 'auth_pass_check',
            interval: 1000,
            onStart: (): void => {
                if (Auth.singleUsePasswordPropagationEnded()) {
                    if (Auth.usedSingleUsePassword() && !Auth.usedKerberos() && Auth.isDefinitelyGenericUser()) {
                        dispatch({
                            type: authActions.TOGGLE_MODAL,
                            passwordModalOpen: true,
                            disableModalClose: true,
                            modalModifiedSubTitle: 'auth:login_page.used_single_use_password',
                        });
                    }
                    timer.stop();
                }
            },
            onTick: (): void => {
                if (Auth.singleUsePasswordPropagationEnded()) {
                    if (Auth.usedSingleUsePassword() && !Auth.usedKerberos() && Auth.isDefinitelyGenericUser()) {
                        dispatch({
                            type: authActions.TOGGLE_MODAL,
                            passwordModalOpen: true,
                            disableModalClose: true,
                            modalModifiedSubTitle: 'auth:login_page.used_single_use_password',
                        });
                    }
                    timer.stop();
                }
            },
        });
    }, [dispatch]);

    useEffect(() => {
        const itemLoader = new ItemLoaders(dispatch);

        if (!Auth.singleUsePasswordPropagationEnded()) {
            if (!timer.isStarted()) {
                timer.start();
            }
        } else {
            if (Auth.usedSingleUsePassword() && !Auth.usedKerberos() && Auth.isDefinitelyGenericUser()) {
                dispatch({
                    type: authActions.TOGGLE_MODAL,
                    passwordModalOpen: true,
                    disableModalClose: true,
                    modalModifiedSubTitle: 'auth:login_page.used_single_use_password',
                });
            }
        }
    }, [dispatch, timer]);

    const error = useMemo(() => {
        return newPassword !== '' && repeatNewPassword !== '' && newPassword !== repeatNewPassword;
    }, [newPassword, repeatNewPassword]);

    const handleSubmit = (): void => {
        if (currentPassword !== '' && newPassword !== '' && repeatNewPassword !== '' && newPassword === repeatNewPassword) {
            const itemLoaders = new ItemLoaders(dispatch);
            window.temporaryPasswordCustomSnackBarFix_DisableDefaultSnackbar = true;
            itemLoaders.putWithCallback(
                FormGeneralHelper.getUrl('/platform_users/' + Auth.getUserId(), 'platform'),
                authActions.CHANGE_PASSWORD,
                { newPassword: newPassword },
                {
                    onSuccess: () => {
                        hideModal();
                        Auth.clearSingleUseState();
                    },
                    onError: (e: any) => {
                        const message = e.message?.message?.split('\n');
                        if (!message) {
                            return;
                        }

                        const output = (
                            <div>
                                {message.map((item: any, key: number) => {
                                    return (
                                        <div key={'constraint-item-' + key}>
                                            {item}
                                            <br />
                                        </div>
                                    );
                                })}
                            </div>
                        );

                        enqueueSnackbar(output, {
                            preventDuplicate: true,
                            key: new Date().getTime() + Math.random(),
                            variant: 'error',
                            action: (key) => (
                                <IconButton key="close" aria-label="close" color="inherit" onClick={(): void => closeSnackbar(key)}>
                                    <Close />
                                </IconButton>
                            ),
                        });

                        setTimeout(() => {
                            window.temporaryPasswordCustomSnackBarFix_DisableDefaultSnackbar = false;
                        }, 1000);
                    },
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false, dispatchErrorAfterCallback: false }
            );
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.target;
        switch (name) {
            case 'current_password':
                setCurrentPassword(value);
                break;
            case 'new_password':
                setNewPassword(value);
                break;
            case 'repeat_new_password':
                setRepeatNewPassword(value);
                break;
            default:
                break;
        }
    };

    const hideModal = (): void => {
        setCurrentPassword('');
        setNewPassword('');
        setRepeatNewPassword('');
        dispatch({ type: authActions.TOGGLE_MODAL, passwordModalOpen: false });
    };

    return (
        <Dialog fullWidth={true} maxWidth="sm" open={passwordModalOpen ?? false} keepMounted onClose={disableModalClose ? (): void => {} : hideModal}>
            <DialogTitle id="alert-dialog-slide-title">
                <h4>
                    <I18n trans={'auth:password_change.password_change_title'} />
                </h4>
            </DialogTitle>
            <DialogContent>
                <GridContainer justify="center">
                    {modalModifiedSubTitle && (
                        <GridItem xs={12} style={{ textAlign: 'center' }}>
                            <h4>
                                <I18n trans={modalModifiedSubTitle} />
                            </h4>
                        </GridItem>
                    )}
                    <GridItem xs={12} style={{ textAlign: 'center' }}>
                        <CustomInputPassword
                            labelText="auth:password_change.current_password"
                            id="current_password"
                            type="password"
                            name="current_password"
                            onChange={handleChange}
                            style={{ width: '100%' }}
                            inputProps={{
                                value: currentPassword,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <LockIcon style={{width: 24, height: 24}}/>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </GridItem>
                    <GridItem xs={8} style={{ textAlign: 'center' }}>
                        <CustomInputPassword
                            labelText="auth:password_change.new_password"
                            id="new_password"
                            type="password"
                            name="new_password"
                            onChange={handleChange}
                            style={{ width: '100%' }}
                            error={error}
                            inputProps={{
                                value: newPassword,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <LockIcon style={{width: 24, height: 24}}/>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </GridItem>
                    <GridItem xs={8} style={{ textAlign: 'center' }}>
                        <CustomInputPassword
                            labelText="auth:password_change.repeat_new_password"
                            id="repeat_new_password"
                            type="password"
                            name="repeat_new_password"
                            onChange={handleChange}
                            style={{ width: '100%' }}
                            error={error}
                            helpText={error ? i18n.t('auth:password_change.passwords_dont_match') : ''}
                            inputProps={{
                                value: repeatNewPassword,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <LockIcon style={{width: 24, height: 24}}/>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </GridItem>
                </GridContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleSubmit} color="primary">
                    <I18n trans={'general:common.save'} />
                </Button>
                {!disableModalClose && (
                    <Button onClick={hideModal} color="secondary">
                        <I18n trans={'general:common.close'} />
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default PasswordChangeModal;
